import { useAuth } from "@clerk/clerk-react";
import { useMemo } from "react";
import type { UserSubscription, SubscriptionPlan, UsageData } from "../hooks/use-subscription";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://flowcus-api-prod.prizio.workers.dev";

export interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

export interface FeatureAccessResult {
  allowed: boolean;
  reason?: string;
  upgrade_required?: boolean;
  current_usage?: number;
  limit?: number;
}

export interface EphemeralToken {
  token: string;
  expires_at: string;
}

/**
 * Custom error classes for subscription-related failures
 */
export class SubscriptionLimitError extends Error {
  constructor(
    public limitType: "todos" | "projects" | "api_cost",
    public currentUsage: number,
    public limit: number,
    public upgradeRequired: boolean = true
  ) {
    super(`${limitType} limit exceeded: ${currentUsage}/${limit}`);
    this.name = "SubscriptionLimitError";
  }
}

export class SubscriptionAuthError extends Error {
  constructor(
    message: string,
    public code?: string
  ) {
    super(message);
    this.name = "SubscriptionAuthError";
  }
}

export class SubscriptionNotFoundError extends Error {
  constructor(message: string = "No active subscription found") {
    super(message);
    this.name = "SubscriptionNotFoundError";
  }
}

/**
 * Result pattern for type-safe error handling
 */
export type Result<T, E = Error> = { success: true; data: T } | { success: false; error: E };

export class APIClient {
  private baseUrl: string;
  private getToken: (forceRefresh?: boolean) => Promise<string | null>;

  constructor(baseUrl: string, getToken: (forceRefresh?: boolean) => Promise<string | null>) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryAttempt = false
  ): Promise<APIResponse<T>> {
    try {
      const token = await this.getToken();

      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      };

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          typeof errorData.error === "string"
            ? errorData.error
            : `HTTP ${response.status}: ${response.statusText}`;

        // Handle subscription-specific error codes
        switch (response.status) {
          case 401:
            // Try token refresh if this is a token expiration error and we haven't retried yet
            if (
              (errorMessage.includes("expired") ||
                errorMessage.includes("invalid") ||
                errorMessage.includes("unauthorized")) &&
              !retryAttempt
            ) {
              try {
                const newToken = await this.getToken(true); // Force refresh
                if (newToken) {
                  // Retry the request with the new token
                  return this.makeRequest(endpoint, options, true);
                }
              } catch (refreshError) {}
            }
            throw new SubscriptionAuthError(errorMessage, errorData.code);
          case 402:
            // Map backend action types to frontend limit types
            const actionToLimitType = {
              create_todo: "todos",
              create_project: "projects",
              api_cost: "api_cost",
            } as const;

            const limitType =
              actionToLimitType[errorData.action as keyof typeof actionToLimitType] || "api_cost";

            throw new SubscriptionLimitError(
              limitType,
              errorData.current_usage || 0,
              errorData.limit || 0,
              errorData.upgrade_required || true
            );
          case 404:
            if (endpoint.includes("/subscriptions/")) {
              throw new SubscriptionNotFoundError(errorMessage);
            }
            throw new Error(errorMessage);
          default:
            throw new Error(errorMessage);
        }
      }

      const data = await response.json();
      return { success: true, data: data.data || data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Enhanced request method with Result pattern for subscription operations
   */
  private async makeSubscriptionRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryAttempt = false
  ): Promise<
    Result<T, SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error>
  > {
    try {
      const token = await this.getToken();
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      };

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          typeof errorData.error === "string"
            ? errorData.error
            : `HTTP ${response.status}: ${response.statusText}`;

        switch (response.status) {
          case 401:
            // Try token refresh if this is a token expiration error and we haven't retried yet
            if (
              (errorMessage.includes("expired") ||
                errorMessage.includes("invalid") ||
                errorMessage.includes("unauthorized")) &&
              !retryAttempt
            ) {
              try {
                const newToken = await this.getToken(true); // Force refresh
                if (newToken) {
                  // Retry the request with the new token
                  return this.makeSubscriptionRequest(endpoint, options, true);
                }
              } catch (refreshError) {}
            }
            return {
              success: false,
              error: new SubscriptionAuthError(errorMessage, errorData.code),
            };
          case 402:
            return {
              success: false,
              error: new SubscriptionLimitError(
                errorData.action || "unknown",
                errorData.current_usage || 0,
                errorData.limit || 0,
                errorData.upgrade_required || true
              ),
            };
          case 404:
            return {
              success: false,
              error: new SubscriptionNotFoundError(errorData.error || "Subscription not found"),
            };
          default:
            return {
              success: false,
              error: new Error(
                errorData.error || `HTTP ${response.status}: ${response.statusText}`
              ),
            };
        }
      }

      const data = await response.json();

      // Handle 404 for subscription endpoints as successful null result
      if (response.status === 404 && endpoint.includes("/subscriptions/current")) {
        return { success: true, data: null as unknown as T };
      }

      return { success: true, data: data.data || data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  // Todos API
  async getTodos(filters?: {
    status?: string;
    priority?: string;
    project_id?: string;
    filter_type?: string;
    time_available?: number;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, value.toString());
        }
      });
    }
    // Add default pagination if not provided
    if (!filters?.limit) {
      params.append("limit", "10");
    }
    if (!filters?.offset) {
      params.append("offset", "0");
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    return this.makeRequest(`/api/v1/todos${query}`);
  }

  async createTodo(todo: {
    title: string;
    description?: string;
    due_date?: string;
    priority?: "low" | "medium" | "high" | "urgent";
    project_id?: string;
    tags?: string[];
    estimated_minutes?: number;
    notes?: string;
    // ADHD-specific fields
    adhd_energy_level?: "low" | "medium" | "high";
    adhd_context_cost?: number;
    adhd_hyperfocus_compatible?: boolean;
  }) {
    // Ensure tags is always an array (matches schema default)
    const todoData = {
      ...todo,
      tags: todo.tags || [],
    };

    return this.makeRequest("/api/v1/todos", {
      method: "POST",
      body: JSON.stringify(todoData),
    });
  }

  async updateTodo(id: string, updates: any) {
    return this.makeRequest(`/api/v1/todos/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteTodo(id: string) {
    return this.makeRequest(`/api/v1/todos/${id}`, {
      method: "DELETE",
    });
  }

  async updateTodoStatus(id: string, status: string) {
    return this.makeRequest(`/api/v1/todos/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
  }

  async getFocusSuggestions(available_minutes?: number) {
    const params = available_minutes ? `?available_minutes=${available_minutes}` : "";
    return this.makeRequest(`/api/v1/todos/focus-suggestions${params}`);
  }

  // Projects API
  async getProjects() {
    return this.makeRequest("/api/v1/projects");
  }

  async createProject(project: {
    name: string;
    description?: string;
    color?: string;
    deadline?: string;
  }) {
    return this.makeRequest("/api/v1/projects", {
      method: "POST",
      body: JSON.stringify(project),
    });
  }

  async updateProject(id: string, updates: any) {
    return this.makeRequest(`/api/v1/projects/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteProject(id: string) {
    return this.makeRequest(`/api/v1/projects/${id}`, {
      method: "DELETE",
    });
  }

  async getTodosCount() {
    return this.makeRequest("/api/v1/todos/count");
  }

  async getProjectsCount() {
    return this.makeRequest("/api/v1/projects/count");
  }

  // Generic HTTP methods
  async get<T = any>(
    endpoint: string,
    options?: { params?: Record<string, any> }
  ): Promise<APIResponse<T>> {
    const url = options?.params
      ? `${endpoint}?${new URLSearchParams(Object.entries(options.params).map(([k, v]) => [k, String(v)]))}`
      : endpoint;
    return this.makeRequest<T>(url);
  }

  async post<T = any>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string): Promise<APIResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: "DELETE",
    });
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // User & System APIs
  async getAuthStatus() {
    return this.makeRequest("/api/v1/auth/status");
  }

  async getUserPreferences() {
    return this.makeRequest("/api/v1/user/preferences");
  }

  async updateUserPreferences(preferences: any) {
    return this.makeRequest("/api/v1/user/preferences", {
      method: "PUT",
      body: JSON.stringify(preferences),
    });
  }

  async getCurrentCosts() {
    return this.makeRequest("/api/v1/cost/current");
  }

  async getLiveAPIToken(): Promise<Result<EphemeralToken, Error>> {
    try {
      const response = await this.makeRequest<EphemeralToken>("/api/v1/live-api/token", {
        method: "POST",
      });

      if (response.success) {
        return { success: true, data: response.data! };
      } else {
        return {
          success: false,
          error: new Error(response.error || "Failed to get ephemeral token"),
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Failed to get ephemeral token"),
      };
    }
  }

  // Subscription API Methods

  /**
   * Get current user subscription with enhanced error handling
   */
  async getCurrentSubscription(): Promise<
    Result<
      UserSubscription | null,
      SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error
    >
  > {
    return this.makeSubscriptionRequest<UserSubscription>("/api/v1/subscriptions/current");
  }

  /**
   * Get available subscription plans
   */
  async getSubscriptionPlans(): Promise<
    Result<
      SubscriptionPlan[],
      SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error
    >
  > {
    return this.makeSubscriptionRequest<SubscriptionPlan[]>(
      "/api/v1/subscriptions/plans?active_only=true"
    );
  }

  /**
   * Get current usage data
   */
  async getSubscriptionUsage(): Promise<
    Result<
      UsageData,
      SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error
    >
  > {
    return this.makeSubscriptionRequest<UsageData>("/api/v1/subscriptions/usage");
  }

  /**
   * Get actual usage counts from database (single source of truth)
   */
  async getUsageCounts(): Promise<
    Result<
      { actual_counts: { todos: number; projects: number } },
      SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error
    >
  > {
    return this.makeSubscriptionRequest<{ actual_counts: { todos: number; projects: number } }>(
      "/api/v1/subscriptions/usage/counts"
    );
  }

  /**
   * Check if user can perform a specific action
   */
  async checkFeatureAccess(
    action: "create_todo" | "create_project" | "api_cost",
    amount: number = 1
  ): Promise<
    Result<
      FeatureAccessResult,
      SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error
    >
  > {
    return this.makeSubscriptionRequest<FeatureAccessResult>(
      `/api/v1/subscriptions/limits/check?action=${action}&amount=${amount}`
    );
  }

  /**
   * Create Stripe checkout session
   */
  async createCheckoutSession(
    planId: string,
    billingCycle: "monthly" | "yearly" | "lifetime"
  ): Promise<
    Result<
      { checkout_url: string; session_id: string },
      SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error
    >
  > {
    return this.makeSubscriptionRequest<{ checkout_url: string; session_id: string }>(
      "/api/v1/subscriptions/checkout",
      {
        method: "POST",
        body: JSON.stringify({
          plan_id: planId,
          billing_cycle: billingCycle,
          success_url: `${window.location.origin}/subscription/success`,
          cancel_url: `${window.location.origin}/subscription/cancel`,
        }),
      }
    );
  }

  /**
   * Create billing portal session
   */
  async createBillingPortalSession(): Promise<
    Result<
      { billing_portal_url: string },
      SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error
    >
  > {
    return this.makeSubscriptionRequest<{ billing_portal_url: string }>(
      "/api/v1/subscriptions/billing-portal",
      {
        method: "POST",
        body: JSON.stringify({
          return_url: `${window.location.origin}/subscription`,
        }),
      }
    );
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(): Promise<
    Result<
      UserSubscription,
      SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error
    >
  > {
    return this.makeSubscriptionRequest<UserSubscription>("/api/v1/subscriptions/cancel", {
      method: "DELETE",
    });
  }

  /**
   * Enhanced todo creation with subscription check
   */
  async createTodoWithCheck(todo: {
    title: string;
    description?: string;
    due_date?: string;
    priority?: "low" | "medium" | "high";
    project_id?: string;
    tags?: string[];
  }): Promise<
    Result<any, SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error>
  > {
    // First check if user can create todos
    const accessCheck = await this.checkFeatureAccess("create_todo", 1);

    if (!accessCheck.success) {
      return accessCheck;
    }

    if (!accessCheck.data.allowed) {
      return {
        success: false,
        error: new SubscriptionLimitError(
          "todos",
          accessCheck.data.current_usage || 0,
          accessCheck.data.limit || 0,
          accessCheck.data.upgrade_required || true
        ),
      };
    }

    // Proceed with todo creation
    return this.makeSubscriptionRequest("/api/v1/todos", {
      method: "POST",
      body: JSON.stringify(todo),
    });
  }

  /**
   * Enhanced project creation with subscription check
   */
  async createProjectWithCheck(project: {
    name: string;
    description?: string;
    color?: string;
    deadline?: string;
  }): Promise<
    Result<any, SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error>
  > {
    // First check if user can create projects
    const accessCheck = await this.checkFeatureAccess("create_project", 1);

    if (!accessCheck.success) {
      return accessCheck;
    }

    if (!accessCheck.data.allowed) {
      return {
        success: false,
        error: new SubscriptionLimitError(
          "projects",
          accessCheck.data.current_usage || 0,
          accessCheck.data.limit || 0,
          accessCheck.data.upgrade_required || true
        ),
      };
    }

    // Proceed with project creation
    return this.makeSubscriptionRequest("/api/v1/projects", {
      method: "POST",
      body: JSON.stringify(project),
    });
  }
}

// Hook to use the API client with authentication
export function useAPIClient() {
  const { getToken } = useAuth();

  // Memoize the API client to prevent unnecessary recreations
  // Move the token function inside useMemo to avoid useCallback overhead
  const client = useMemo(() => {
    // Get token with proper error handling and refresh capability
    const getTokenWithTemplate = async (forceRefresh: boolean = false) => {
      try {
        // Force refresh if requested, otherwise use cached token
        // Request the flowcus-api JWT template for custom claims
        const token = await getToken({
          skipCache: forceRefresh,
          template: "flowcus-api",
        });
        return token;
      } catch (error) {
        return null;
      }
    };

    return new APIClient(API_BASE_URL, getTokenWithTemplate);
  }, [getToken]);

  return client;
}
